import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { handleAnalytics, Events } from '@foxcorp/delta-web-analytics';
import Modal from 'componentsV2/Modal';
import Button from 'componentsV2/Button';
import PropTypes from 'prop-types';
import useBreakpoint from 'hooks/useBreakpoint';
import Loader from 'components/Loader';
import { isMobileBreakpoint } from 'helpers/BreakpointHelper';
import { redirectToExternalUrl, helperGetStaticContentUrl } from 'helpers/RouteHelper';
import { isSeries, isEpisode } from 'helpers/ContentHelper';
import {
  SERIES_DETAIL_HERO_IMAGE,
  SERIES_DETAIL_LOGO_IMAGE,
  getDynamicImage,
} from 'helpers/ImageHelper';
import { fetchSeries, clearSeriesDetail } from 'screens/ContentDetail/ContentDetailActions';
import style from './MobileWebRoadblock.module.scss';

const MobileWebRoadblock = ({ account, deviceId: currentDeviceId, item }) => {
  const { profile } = useSelector(state => state?.app);
  const { seriesData } = useSelector(state => state?.seriesDetail);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const breakpoint = useBreakpoint();
  const isMobile = isMobileBreakpoint(breakpoint);
  const { mobile_web_roadblock_directive, mobile_web_roadblock_button_cta } = window.Language;
  const accessToken = account?.accessToken;
  const deviceId = currentDeviceId ?? account?.deviceId;
  const profileId = account?.profileId;
  const currentItem = seriesData || item;
  const images = currentItem?.images;
  const logoImageUrl = images?.logoCenter?.raw;
  const heroAltText = images?.still?.altText ?? '';
  const logoAltText = images?.logoCenter?.altText ?? '';
  const seriesType = currentItem?.seriesType;
  const title = currentItem?.name;
  const fallbackImage = helperGetStaticContentUrl('images', 'FallBack-FOXNATION-min.jpg');
  const heroImageUrl = images?.still?.raw ?? images?.seriesDetail?.raw ?? fallbackImage;
  let headline;
  if (isEpisode(currentItem?.videoType)) {
    headline = currentItem?.latestEpisode?.name ?? currentItem?.name;
  } else if (isSeries(seriesType)) {
    headline = `Season ${currentItem?.seasonCount} (${currentItem?.fullEpisodeCount}) episodes`;
  }

  const heroImage = getDynamicImage({
    breakpoint,
    context: SERIES_DETAIL_HERO_IMAGE,
    url: heroImageUrl,
  });
  const logoImage = getDynamicImage({
    breakpoint,
    context: SERIES_DETAIL_LOGO_IMAGE,
    url: logoImageUrl,
  });

  useEffect(() => {
    const shouldFetchSeries = !heroImageUrl || !logoImageUrl;
    if (shouldFetchSeries && !!currentItem?.showCode) {
      setIsLoading(true);
      (async () => {
        await dispatch(fetchSeries(currentItem?.showCode, accessToken, deviceId));
        setIsLoading(false);
      })();
    }
    return () => {
      dispatch(clearSeriesDetail());
    };
  }, [fetchSeries, accessToken, deviceId, clearSeriesDetail, dispatch]);

  useEffect(() => {
    if (currentItem) {
      handleAnalytics({
        event: Events.ROADBLOCK_LANDING,
        payload: {
          page_name: 'delta:web:landing:roadblock',
          page_type: 'roadblock',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isMobile) return null;

  const onPrimaryClick = async () => {
    handleAnalytics({
      event: Events.BUTTON_LINK_SELECTED,
      payload: {
        page_button_text: mobile_web_roadblock_button_cta,
        page_name: 'delta:web:landing:roadblock',
        page_type: 'roadblock',
      },
    });
    const defaultDeeplink = `https://foxnation.onelink.me/CbbD/InstallApp`;
    let primaryUrl;
    const isLoggedIn = accessToken && profileId && deviceId;
    if (isLoggedIn) {
      const { code: regCode = '' } = await profile?.regCodeDeeplink({ deviceId });
      if (regCode) {
        primaryUrl = `${defaultDeeplink}?deep_link_sub1=${regCode}`;
      }
    }
    if (!primaryUrl) primaryUrl = defaultDeeplink;
    redirectToExternalUrl(primaryUrl);
  };

  return (
    <Modal theme={style} showModal>
      {!isLoading ? (
        <>
          <img className={style.seriesImage} src={heroImage} alt={heroAltText} />
          {logoImage ? (
            <div className={style.logoContainer}>
              <img className={style.logo} src={logoImage} alt={logoAltText} />
            </div>
          ) : (
            <h2 className={style.title}>{title}</h2>
          )}
          {headline && (
            <>
              <h4 className={style.headline}>{headline}</h4>
              <div className={style.divider} />
            </>
          )}
          <div className={style.detailsContainer}>
            <h4>{mobile_web_roadblock_directive}</h4>
            <Button primary label={mobile_web_roadblock_button_cta} onClick={onPrimaryClick} />
          </div>
        </>
      ) : (
        <Loader theme={style} />
      )}
    </Modal>
  );
};

MobileWebRoadblock.propTypes = {
  account: PropTypes.shape({}),
  deviceId: PropTypes.string,
  item: PropTypes.shape({}),
};

export default MobileWebRoadblock;
